import Button from '@atoms/Button/Button';
import RadioInput from '@atoms/Inputs/RadioInput';
import functionCategoryFilters from '@config/matchmaker/filters/functionCategoryFilters';
import {
  CATERING_INDUSTRY_FUNCTION_CATEGORY_CODE,
  FACILITY_FUNCTION_CATEGORY_CODE,
  MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
  STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
  VOLUNTEER_APPLICATION_CODE,
} from '@constants';
import { vacancyFiltersType } from '@lib/proptypes';
import MatchMakerQuestion from '@molecules/MatchMaker/MatchMakerQuestion';
import Modal from '@molecules/Modal/Modal';
import InfoCircleIcon from '@svg/info-circle.svg';
import { useMatchmakerQuestion } from '@utils';
import useApplicationFormsAnswer from '@utils/matchmaker/useApplicationFormsAnswer';
import PropTypes from 'prop-types';
import { useState } from 'react';

import styles from './Matchmaker.module.scss';

const MODAL_ID = 'q3-info-modal';
const filterKey = 'functionCategories';

export default function Question3({ filters, handleNavigation }) {
  const [showHelp, setShowHelp] = useState(false);
  const { selectedApplicationFormCode } = useApplicationFormsAnswer(filters);

  const filterIsAvailable = filter =>
    functionCategoryFilters.every(
      functionCategoryFilter =>
        !(
          functionCategoryFilter.applicationForm ===
            selectedApplicationFormCode &&
          !functionCategoryFilter.categories.includes(filter.code)
        )
    );

  const { inputs, handleChange, handlePreviousUrl, handleNextUrl } =
    useMatchmakerQuestion({
      key: filterKey,
      callback: handleNavigation,
      initialState: state => state.functionCategories?.split('-')[0],
      saveStateAs: () => {
        const selected = filters.functionCategories.find(
          filter =>
            inputs.functionCategories === filter.id.toString() &&
            filterIsAvailable(filter)
        );

        if (!selected) return { functionCategories: undefined };

        return {
          functionCategories: `${selected.id}-${selected.code}`,
        };
      },
    });

  const shouldSkipNextQuestion = () => {
    // if the selected application form is volunteer, skip the next question
    if (selectedApplicationFormCode === VOLUNTEER_APPLICATION_CODE) {
      return true;
    }

    const code = filters.functionCategories.find(
      filter =>
        filterIsAvailable(filter) &&
        inputs.functionCategories === filter.id.toString()
    )?.code;

    return [
      FACILITY_FUNCTION_CATEGORY_CODE,
      CATERING_INDUSTRY_FUNCTION_CATEGORY_CODE,
      STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
      MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
    ].includes(code);
  };

  const getNextLink = () =>
    shouldSkipNextQuestion() ? '/matchmaker/5' : '/matchmaker/4';

  return (
    <MatchMakerQuestion
      number={3}
      total={7}
      question="Welk werkveld past het best bij jou?"
      extraClasses={styles.matchMakerQuestion}
    >
      <div className={styles.inputsContainer}>
        {filters.functionCategories
          .filter(filter => filterIsAvailable(filter))
          .map(({ id, title }) => (
            <RadioInput
              key={`vacancy-function-${id}`}
              id={id}
              name={filterKey}
              onChange={handleChange}
              label={title}
              checked={inputs[filterKey] === id.toString()}
              value={id.toString()}
            />
          ))}
      </div>

      <Button
        label="Wat betekent dit?"
        onClick={() => setShowHelp(true)}
        variant="text"
        iconLeft
        Icon={InfoCircleIcon}
        noAnimation
        modifierClass="largeIcon"
        aria-expanded={showHelp}
        aria-controls={MODAL_ID}
      />

      <Modal
        id={MODAL_ID}
        show={showHelp}
        title="Wat betekent dit?"
        onClose={() => setShowHelp(false)}
      >
        <h3 className="u-typography-heading-4 u-mb-1">Welzijn</h3>
        <p>
          We organiseren verschillende welzijnsactiviteiten met daarbij speciale
          aandacht voor kunst en cultuur. Daarnaast bieden we diensten die aan
          het welzijn van ouderen bijdragen.
        </p>

        <h3 className="u-typography-heading-4 u-mb-1">Zorg</h3>
        <p>
          Bij Vitalis bieden we thuiszorg (verzorging, verpleging, behandeling)
          en zorg en wonen op onze locaties als het thuis niet meer gaat. Ook
          bieden we zorg en begeleiding bij bijvoorbeeld revalidatie,
          geestelijke gezondheid en dementie.
        </p>

        <h3 className="u-typography-heading-4 u-mb-1">Behandeling</h3>
        <p>
          Bij Vitalis bieden we de volgende behandelingen aan:
          ouderengeneeskunde, psychologie, logopedie, diëtetiek, fysiotherapie,
          ergotherapie en muziektherapie.
        </p>

        <h3 className="u-typography-heading-4 u-mb-1">Facilitair</h3>
        <p>
          In dit werkveld ben je lekker actief bezig. Je takenpakket is heel
          afwisselend. Denk aan schoonmaakwerkzaamheden en (lichte)
          werkzaamheden in de tuin.
        </p>

        <h3 className="u-typography-heading-4 u-mb-1">Horeca</h3>
        <p>
          In de horeca verzorg maaltijden voor de ouderen en serveer je een
          drankje op de afdeling. Natuurlijk gepaard met een gezellig praatje!
        </p>

        <h3 className="u-typography-heading-4 u-mb-1">Staf & administratief</h3>
        <p>
          We hebben een groot aantal staffuncties. Van ICT en kwaliteit tot
          communicatie en innovatie. En wat dacht je van HR en recruitment? De
          mogelijkheden zijn (bijna) eindeloos.
        </p>
      </Modal>
      <div className={styles.ctaContainer}>
        <Button
          key="matchmaker-back-button"
          label="Terug"
          href="/matchmaker/2"
          size="smallMobile"
          onClick={handlePreviousUrl}
          color="secondary"
        />
        <Button
          key="matchmaker-next-button"
          label="Volgende"
          href={getNextLink()}
          size="smallMobile"
          onClick={handleNextUrl}
          disabled={!inputs.functionCategories}
        />
      </div>
    </MatchMakerQuestion>
  );
}

Question3.propTypes = {
  filters: vacancyFiltersType,
  handleNavigation: PropTypes.func,
};
